import { Box, Typography } from '@mui/material'
import { type documentTypeModel } from '.'
import SimpleAccordion from 'Components/Accordian'
import FilePicker from 'Components/FilePicker'
import S3Image from 'Components/S3Image'
import { type DocumentCaregiver, type DocumentData, type income } from 'core/types'
import { FormatCurrency } from 'core/Utilities'
import { RadiusCard } from 'Components/Cards/Radius/RadiusCard'
import { WarningCircle } from 'Assets/SVGs/WarningCircle'
import { DocumentInfo } from 'Components/DocumentList/DocumentInfo'
import { useState } from 'react'

interface DocumentAccordionProps {
  documentType: documentTypeModel
  editable: boolean
  canVerify: boolean
  downloadDocument?: (id: number) => void
  uploadDocument: (file: File, year: number | null) => void
  onVerifiedChanged: (index: number, verified: boolean, description: string, assignedIds: DocumentCaregiver[],
    incomeAmount: number | null,
    incomeYear: number | null
  ) => Promise<boolean>
  onDelete: (id: number, index: number, verified: boolean) => void
  incomeRecords: income[] | null
}

export function DocumentAccordion (props: DocumentAccordionProps): JSX.Element {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [editingDocIds, setEditingDocIds] = useState<number[]>([])
  const yearOptions: number[] = []

  const currentYear = new Date().getFullYear()
  for (let i = currentYear - 6; i <= currentYear + 1; i++) {
    yearOptions.push(i)
  }
  let displayClass = ''
  if (props.documentType.required && props.documentType.documents.length === 0) {
    displayClass = 'error-fill'
  } else if (props.documentType.required) {
    displayClass = 'warning-fill'
  }
  const image = props.documentType.imageFile !== null ? <S3Image imageKey={props.documentType.imageFile} /> : <></>

  const documentSection = (income: income | null | undefined): JSX.Element => {
    // undefined means income is not applicable, null means filter for documents where no income is assigned, if there is a value for income then show documents tied to it
    let documents = null
    if (income === undefined) {
      documents = props.documentType.documents
    } else if (income === null) {
      documents = props.documentType.documents.filter(d => d.incomeYear === null)
    } else {
      documents = props.documentType.documents.filter(d => d.incomeYear === income.year)
    }
    return <Box>
      {income !== null && income !== undefined && <table>
        <tr className='padless-table'>
          <td>
            <Typography variant='subtitle2' sx={{ whiteSpace: 'nowrap' }}>Taxable Income: {FormatCurrency(income.annualIncome)}</Typography>
          </td>
          {documents.length === 0 &&
            <>
              <td rowSpan={2} style={{ verticalAlign: 'top' }} >
                <WarningCircle fill='red' size={20} />
              </td>
              <td rowSpan={2} >
                No enrollments will be allowed in sessions that require income for this tax year until these documents have been uploaded.
              </td>
            </>
          }
        </tr>
        <tr>
          <td>
            <Typography variant='subtitle2' sx={{ whiteSpace: 'nowrap' }} >Household Member Count: {income.memberCount}</Typography>
          </td>
        </tr>
      </table>}
      {props.editable && income !== null && <Box className='mt-5 mb-5'>
        <FilePicker
          label='Upload a file'
          name='file'
          onSelect={(e: any) => {
            props.uploadDocument(e.target.files[0], income?.year ?? null)
          }}
        />
      </Box>
      }
      {documents.map((d, i) => {
        const isEditing = editingDocIds.find(doc => doc === d.documentID) !== undefined
        return <>
          <DocumentInfo
            yearOptions={yearOptions}
            verifyMode={isEditing}
            setVerifyMode={async (num: number, val: boolean) => {
              if (val) {
                const x = [...editingDocIds, d.documentID]
                setEditingDocIds(x)
              } else {
                const x = editingDocIds.filter(doc => doc !== d.documentID)
                setEditingDocIds(x)
              }
            }}
            onVerify={async (docId: number, data: DocumentData) => {
              const success = await props.onVerifiedChanged(i, true, data.verificationNote ?? '', data.caregivers, data.incomeAmount, data.incomeYear)
              if (success) {
                const x = editingDocIds.filter(doc => doc !== d.documentID)
                setEditingDocIds(x)
              }
            }}
            onUnVerify={async (docId: number, data: DocumentData) => {
              const success = await props.onVerifiedChanged(i, false, data.verificationNote ?? '', data.caregivers, null, null)
              if (success) {
                const x = editingDocIds.filter(doc => doc !== d.documentID)
                setEditingDocIds(x)
              }
            }}
            onDelete={() => { props.onDelete(d.documentID, i, d.verified) }}
            document={d} disabled={!props.canVerify}
            documentID={d.documentID}
            childID={d.childID ?? 0}
          />
        </>
      })

      }
    </Box>
  }

  return <SimpleAccordion
    dataTestid={`documentType${props.documentType.documentTypeID}Accordion`}
    title={props.documentType.name} key={props.documentType.documentTypeID.toString() + 'accordion'}
    width={'100%'}
    icon={image}
    summary={props.documentType.documentDescription ?? ''}
    className={displayClass}
    sx={{
      borderRadius: '32px !important',
      border: '1px solid lightgray',
      mb: '15px',
      ':first-of-type': {
        borderRadius: '16px'
      },
      ':last-of-type': {
        borderRadius: '16px'
      },
      '&.Mui-expanded:last-of-type': {
        mb: '25px'
      }
    }}

    body={
      <Box sx={{ fontWeight: 400, fontSize: '0.75em' }} data-testid={displayClass}>
        {props.editable && <Box>{'Please upload your most recent documentation supporting '} {props.documentType.name} {'.'}
          {(props.documentType.documentDescription ?? '') !== '' ? ` For example ${props.documentType.documentDescription ?? ''}` : ''}</Box>}
        <Typography variant='body1' className='mt-5 mb-5'>Uploaded Documents</Typography>
        {props.incomeRecords?.length}
        {(props.incomeRecords === null || (props.incomeRecords?.length ?? 0) === 0) && documentSection(undefined)}
        {props.incomeRecords?.map((r, i) => { return <RadiusCard content={documentSection(r)} fullWidth={true} header={`Tax Year: ${r.year}`} /> })}
      </Box>
    }
    tag={props.documentType.documentStatus}
  />
}
